import React, {useState, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  InputGroup,
  Form,
  Accordion,
  Alert,
  Button,
} from "react-bootstrap";
import {
  FaSearch,
  FaRocket,
  FaUsers,
  FaCogs,
  FaPaperPlane,
  FaGift,
} from "react-icons/fa";
import {Link} from "react-router-dom";

// Datos de las preguntas frecuentes. Estos podrían venir de una API en el futuro.
const faqData = [
  {
    category: "Primeros Pasos",
    icon: <FaRocket className="me-2 text-success" />,
    questions: [
      {
        q: "¿Cómo envío mi primer comunicado?",
        a: "Para crear tu primer comunicado, ve al menú 'Comunicados' y haz clic en el botón 'Crear Nuevo Comunicado'. Sigue los 3 sencillos pasos: define el contenido, selecciona tus grupos de difusión y, finalmente, revisa y envía.",
      },
      {
        q: "¿Cómo importo a mis colaboradores?",
        a: "Actualmente, puedes añadir colaboradores manualmente desde la sección 'Contactos'. Ve a 'Contactos', haz clic en 'Añadir Contacto' y completa el formulario. La importación masiva desde Excel estará disponible próximamente.",
      },
    ],
  },
  {
    category: "Gestión de Campañas",
    icon: <FaPaperPlane className="me-2 text-success" />,
    questions: [
      {
        q: "¿Qué diferencia hay entre 'Borrador' y 'Enviada'?",
        a: "Un 'Borrador' es una campaña que has guardado pero aún no has enviado. Puedes editarla en cualquier momento. Una campaña 'Enviada' ya ha sido procesada y no puede modificarse, pero puedes ver sus métricas detalladas.",
      },
      {
        q: "¿Puedo programar una campaña para que se envíe más tarde?",
        a: "La funcionalidad de programación de campañas está en desarrollo y será una de nuestras próximas grandes actualizaciones. ¡Mantente atento!",
      },
      {
        q: "¿Qué significan las métricas 'Tasa de Lectura' e 'Incentivos Usados'?",
        a: "'Tasa de Lectura' es el porcentaje de destinatarios que abrieron y leyeron tu mensaje. 'Incentivos Usados' muestra cuántos de los destinatarios que recibieron un incentivo (como un código de descuento) lo han utilizado.",
      },
    ],
  },
  {
    category: "Contactos y Listas",
    icon: <FaUsers className="me-2 text-success" />,
    questions: [
      {
        q: "¿Un contacto puede estar en varias listas de difusión?",
        a: "Sí, un mismo contacto puede pertenecer a múltiples listas. Esto te da flexibilidad para segmentar tus envíos sin tener que duplicar contactos.",
      },
      {
        q: "¿Cómo elimino un contacto de una lista?",
        a: "Ve a 'Listas', haz clic en 'Editar' en la lista que deseas modificar. En el modal de edición, busca al miembro que quieres eliminar y desmarca la casilla junto a su nombre. Finalmente, guarda los cambios.",
      },
    ],
  },
  {
    category: "Estrategia y Mejores Prácticas",
    icon: <FaRocket className="me-2 text-success" />,
    questions: [
      {
        q: "¿Cuál es la mejor hora para enviar mis campañas?",
        a: "No hay una respuesta única, pero el Dashboard te da pistas. Analiza el gráfico de 'Rendimiento por Campaña' para ver qué días y horas han tenido la mejor tasa de lectura. Recomendamos empezar enviando a mitad de mañana (10-11 AM) en días laborales.",
      },
      {
        q: "¿Cómo puedo mejorar mi tasa de lectura?",
        a: "La clave es la relevancia. Usa un nombre de campaña claro y un mensaje conciso. ¡Los elementos interactivos como imágenes o encuestas aumentan mucho la interacción! También, asegúrate de que tus listas de difusión estén bien segmentadas.",
      },
    ],
  },
  {
    category: "Gestión de Incentivos",
    icon: <FaGift className="me-2 text-success" />,
    questions: [
      {
        q: "¿Puedo desactivar un incentivo sin eliminarlo?",
        a: "Sí. En la sección de 'Incentivos', puedes editar un incentivo y cambiar su estado a 'Inactivo'. Esto es útil para ofertas de temporada, ya que no aparecerá en nuevas campañas pero mantendrás su historial.",
      },
    ],
  },
  {
    category: "Configuración",
    icon: <FaCogs className="me-2 text-success" />,
    questions: [
      {
        q: "¿Dónde consigo las credenciales de la API de WhatsApp?",
        a: "Debes obtener tus credenciales (Token de Acceso, ID del Teléfono, etc.) desde tu cuenta de Meta for Developers, dentro de la configuración de tu aplicación de WhatsApp Business API. Pega esos valores en la sección 'Configuración' de PorterCom.",
      },
    ],
  },
];

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredFaqData = useMemo(() => {
    if (!searchTerm) {
      return faqData;
    }

    const lowercasedFilter = searchTerm.toLowerCase();
    const filtered = faqData
      .map((category) => {
        const filteredQuestions = category.questions.filter(
          (item) =>
            item.q.toLowerCase().includes(lowercasedFilter) ||
            item.a.toLowerCase().includes(lowercasedFilter)
        );

        if (filteredQuestions.length > 0) {
          return {...category, questions: filteredQuestions};
        }
        return null;
      })
      .filter(Boolean); // Elimina las categorías que quedaron vacías

    return filtered;
  }, [searchTerm]);

  return (
    <Container fluid className="bg-light">
      {/* Encabezado central con buscador */}
      <div className="text-center py-5 px-3 bg-gradient-green rounded-bottom-4 mb-5">
        <h1 className="display-5 fw-bold">¿Cómo podemos ayudarte?</h1>
        <p className="lead text-muted mb-4">
          Busca en nuestra base de conocimiento para encontrar respuestas al
          instante.
        </p>
        <Row className="justify-content-center">
          <Col lg={6}>
            <InputGroup size="lg" className="shadow-sm">
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="Busca una pregunta (ej: 'enviar comunicado')"
                className="border-start-0"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
      </div>

      {/* Contenido de las FAQs */}
      <Container>
        {filteredFaqData.map((category, index) => (
          <Card key={index} className="shadow-sm rounded-4 border-0 mb-4">
            <Card.Body className="p-4">
              <h4 className="fw-bold mb-3 d-flex align-items-center">
                {category.icon} {category.category}
              </h4>
              <Accordion flush>
                {category.questions.map((item, qIndex) => (
                  <Accordion.Item
                    eventKey={qIndex.toString()}
                    key={qIndex}
                    className="mb-2 border rounded-3"
                  >
                    <Accordion.Header>{item.q}</Accordion.Header>
                    <Accordion.Body className="text-muted">
                      {item.a}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Card.Body>
          </Card>
        ))}

        {/* Alert para contacto de soporte */}
        <Alert
          variant="light"
          className="mt-5 text-center border-success border-2 rounded-4 p-4"
        >
          <Alert.Heading className="fw-bold">
            ¿No encuentras lo que buscas?
          </Alert.Heading>
          <p>Nuestro equipo de soporte está listo para ayudarte.</p>
          <Button variant="success" as={Link} to="/support">
            Contactar a Soporte
          </Button>
        </Alert>
      </Container>
    </Container>
  );
};

export default HelpCenter;
